import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { assignDefinedFields } from '../../utils/assign-fields';
import { assignMetadata } from '../../utils/assign-metadata';
import {
  createApiIntegration,
  createApiIntegrationOperation,
  deleteApiIntegration,
  deleteApiIntegrationOperation,
  updateApiIntegration,
  updateApiIntegrationConfig,
  updateApiIntegrationOperation,
} from './actions';
import { INITIAL_STATE } from './constants';
import type {
  ApiIntegration,
  ApiIntegrationOperation,
  AuthType,
} from './types';

const apiIntegrationsAdapter = createEntityAdapter<ApiIntegration>();
const apiIntegrationsSelectors = apiIntegrationsAdapter.getSelectors();

const slice = createSlice({
  name: 'apiIntegrations',
  initialState: INITIAL_STATE,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(createApiIntegration, (state, action) => {
      const newApiIntegration: ApiIntegration = {
        operations: {},
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
        ...action.payload,
        environments: {
          sandbox: action.payload.environments?.sandbox ?? {
            baseUrl: '',
            authType: 'none',
          },
          'pre-release': action.payload.environments?.preRelease ?? {
            baseUrl: '',
            authType: 'none',
          },
          live: action.payload.environments?.live ?? {
            baseUrl: '',
            authType: 'none',
          },
        },
        description: action.payload.description ?? '',
      };

      const newApiIntegrationWithMetadata = assignMetadata(
        action,
        newApiIntegration,
      );
      apiIntegrationsAdapter.addOne(
        state.apiIntegrations,
        newApiIntegrationWithMetadata,
      );
    });

    builder.addCase(deleteApiIntegration, (state, action) => {
      apiIntegrationsAdapter.removeOne(
        state.apiIntegrations,
        action.payload.id,
      );
    });

    builder.addCase(updateApiIntegration, (state, action) => {
      const existingIntegration =
        state.apiIntegrations.entities[action.payload.id];

      if (existingIntegration) {
        const updatedIntegration = assignDefinedFields(existingIntegration, {
          ...action.payload,
        });
        const updatedIntegrationWithMetadata = assignMetadata(
          action,
          updatedIntegration,
        );
        apiIntegrationsAdapter.updateOne(state.apiIntegrations, {
          id: action.payload.id,
          changes: updatedIntegrationWithMetadata,
        });
      }
    });

    builder.addCase(updateApiIntegrationConfig, (state, action) => {
      const existingIntegration =
        state.apiIntegrations.entities[action.payload.id];

      const existingConfig =
        existingIntegration?.environments[action.payload.environment];
      if (existingConfig) {
        const dataToUpdate = {
          authType: action.payload.authType as AuthType,
          baseUrl: action.payload.baseUrl,
        };
        const updatedConfig = assignDefinedFields(existingConfig, dataToUpdate);

        const updatedIntegration = assignDefinedFields(existingIntegration, {
          environments: { [action.payload.environment]: updatedConfig },
        });
        const updatedIntegrationWithMetadata = assignMetadata(
          action,
          updatedIntegration,
        );

        apiIntegrationsAdapter.updateOne(state.apiIntegrations, {
          id: action.payload.id,
          changes: updatedIntegrationWithMetadata,
        });
      }
    });

    builder.addCase(createApiIntegrationOperation, (state, action) => {
      const integration =
        state.apiIntegrations.entities[action.payload.integrationId];

      if (integration) {
        const newOperation: ApiIntegrationOperation = {
          id: action.payload.id,
          createdAt: '',
          createdBy: '',
          updatedAt: '',
          updatedBy: '',
          method: action.payload.method,
          resource: action.payload.resource,
          name: action.payload.name,
        };

        const operationWithMetadata = assignMetadata(action, newOperation);
        integration.operations[operationWithMetadata.id] =
          operationWithMetadata;

        const updatedIntegrationWithMetadata = assignMetadata(
          action,
          integration,
        );
        apiIntegrationsAdapter.updateOne(state.apiIntegrations, {
          id: action.payload.integrationId,
          changes: updatedIntegrationWithMetadata,
        });
      }
    });

    builder.addCase(updateApiIntegrationOperation, (state, action) => {
      const integration =
        state.apiIntegrations.entities[action.payload.integrationId];

      if (integration) {
        const operationId = action.payload.id;
        const operation = integration.operations[operationId];

        if (operation) {
          const updatedOperation = assignDefinedFields(
            operation,
            action.payload,
          );
          const operationWithMetadata = assignMetadata(
            action,
            updatedOperation,
          );
          integration.operations[operationId] = operationWithMetadata;

          const updatedIntegrationWithMetadata = assignMetadata(
            action,
            integration,
          );
          apiIntegrationsAdapter.updateOne(state.apiIntegrations, {
            id: action.payload.integrationId,
            changes: updatedIntegrationWithMetadata,
          });
        }
      }
    });

    builder.addCase(deleteApiIntegrationOperation, (state, action) => {
      const integration =
        state.apiIntegrations.entities[action.payload.integrationId];

      if (integration) {
        assignMetadata(action, integration);
        const newOperations = { ...integration.operations };
        delete newOperations[action.payload.id];
        integration.operations = newOperations;
        apiIntegrationsAdapter.updateOne(state.apiIntegrations, {
          id: action.payload.integrationId,
          changes: integration,
        });
      }
    });
  },
});

export default slice.reducer;

export { apiIntegrationsAdapter, apiIntegrationsSelectors };
